<template>
    <div class="card shadow mb-4">
        <div class="card-header">
            {{ $t('form.labels.location') }}
            <div class="float-end">
                <ParameterHelp :estateType="estateType" name="location"/>
            </div>
        </div>
        <div class="card-body">
            <LocationSearchControl class="cursor-default" :value="value" @input="onInput" :key="'search' + clearCounter"/>
            <div class="invalid-feedback d-block" v-if="invalid">{{ $t('form.errors.locationNotFilled') }}</div>

            <div class="location-map">
                <EstatesMap :key="value !== null ? value.id : null" :main-estate="selectedPlace"/>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import LocationSearchControl from '@/components/common/LocationSearchControl.vue';
import ParameterHelp from '@/components/estimates/common/ParameterHelp.vue';
import EstatesMap from '@/components/maps/EstatesMap.vue';
import {Place} from '@/components/maps/Place';
import {Segment} from '@/composables/Segment';
import {EstimateEstateType, Location, RealEstateEstimateState} from '@/model/api';
import {Estimate} from '@/model/Estimate';
import {EVENT_LOCATION_VALIDATED, EventBus} from '@/model/events';
import {left, right} from 'fp-ts/lib/Either';
import {isEqual} from 'lodash-es';
import {computed, defineComponent, inject, PropType, ref} from 'vue';

export default defineComponent({
    components: {LocationSearchControl, EstatesMap, ParameterHelp},
    props: {
        defaultValue: {
            type: Object as PropType<Location | null>,
            default: null,
        },
        estateType: {
            type: String as PropType<EstimateEstateType>,
            required: true,
        },
    },
    setup(props, {expose}) {
        const eventBus = inject<EventBus>('eventBus')!;
        const model = inject<Estimate<RealEstateEstimateState>>('model')!;

        const validate = ref(false);
        const value = ref<Location | null>(null);
        const clearCounter = ref(0);
        const originalValue = ref<Location | null>(props.defaultValue);

        const invalid = computed(() => validate.value && value.value === null);

        const selectedPlace = computed<Place | null>(() => {
            const v = value.value;

            if (v === null) {
                return null;
            }

            const point = v.center;

            if (point === null) {
                return null;
            }

            return {
                title: v.name,
                coordinates: [point.coordinates[1], point.coordinates[0]],
            };
        });

        // created logic
        value.value = props.defaultValue;
        originalValue.value = props.defaultValue;

        function onInput(location: Location): void {
            value.value = location;
        }

        async function submit() {
            const v = value.value;

            if (v === null) {
                validate.value = true;

                return left(null);
            }

            eventBus.emit(EVENT_LOCATION_VALIDATED, v);

            const estimate = await model.selectLocation(v);

            validate.value = false;
            originalValue.value = value.value;

            return right(estimate);
        }

        function hasNotSubmittedChanges(): boolean {
            return !isEqual(value.value, originalValue.value);
        }

        function clearNotSubmittedChanges(): void {
            value.value = originalValue.value;
            clearCounter.value++;
        }

        function allowsMovingOn(): boolean {
            return props.defaultValue !== null;
        }

        function beforeEnter(): void {
        }

        const segmentApi: Segment<RealEstateEstimateState> = {
            hasNotSubmittedChanges,
            clearNotSubmittedChanges,
            submit,
            allowsMovingOn,
            beforeEnter,
        };

        expose(segmentApi);

        return {
            value,
            validate,
            clearCounter,
            invalid,
            selectedPlace,
            onInput,
            ...segmentApi,
        };
    },
});
</script>
